import { Loader } from "lucide-react"
import { useTranslation } from "react-i18next"

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@f2e/ui/components/base"

import { PermissionButton } from "@/components/Auth"
import { Member, Organization, Permission, User, Workspace } from "@/types"

import MemberDialog from "./MemberDialog"
import MemberTable from "./MemberTable"

export interface WorkspaceMembersDialogProps {
  organization: Organization
  workspace?: Workspace
  members: Member[]
  membersLoaded: boolean
  membersError: unknown
  open: boolean
  addDialogOpen: boolean
  onOpenChange: (open: boolean) => void
  onAddDialogOpenChange: (open: boolean) => void
  onConfirmAddMembers: (newUsers: User[]) => void
  onLeaveMember?: (member: Member) => void
}

const WorkspaceMembersDialog = ({
  organization,
  workspace,
  members,
  membersLoaded,
  membersError,
  open,
  addDialogOpen,
  onOpenChange,
  onAddDialogOpenChange,
  onConfirmAddMembers,
  onLeaveMember,
}: WorkspaceMembersDialogProps) => {
  const { t } = useTranslation()

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="flex max-h-[min(90vh,calc(100dvh-2rem))] w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl"
          aria-describedby={undefined}
        >
          <DialogHeader className="shrink-0 px-6 py-4 border-b flex flex-row items-center justify-between">
            <DialogTitle>{t("workspace.detail.tabs.members")}</DialogTitle>
            {workspace ? (
              <PermissionButton
                permission={Permission.WsAddMembers}
                workspace={workspace}
                disabledTooltip={t("common.noPermission")}
                size="sm"
                onClick={() => onAddDialogOpenChange(true)}
                className="mr-8 bg-slate-900 text-white hover:bg-slate-900/90"
              >
                <span className="mr-2">+</span>
                {t("workspace.new", { defaultValue: "New" })}
              </PermissionButton>
            ) : null}
          </DialogHeader>
          <div
            className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-6"
            data-testid="workspace-members-dialog-body"
          >
            {!workspace ? null : !membersLoaded && !membersError ? (
              <Loader className="inline mr-2 mb-1 h-4 w-4 animate-spin" />
            ) : membersError ? (
              <div className="text-sm text-red-600">
                {t("workspace.detail.errors.loadFailed")}
              </div>
            ) : (
              <MemberTable
                workspace={workspace}
                members={members}
                pageInfo={{
                  current: 1,
                  totalPages: 1,
                  totalRecords: members.length,
                  limit: 50,
                  offset: 0,
                }}
                onLeave={onLeaveMember}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {workspace ? (
        <MemberDialog
          organization={organization}
          currentMembers={members}
          open={addDialogOpen}
          onOpenChange={onAddDialogOpenChange}
          onConfirm={onConfirmAddMembers}
        />
      ) : null}
    </>
  )
}

export { WorkspaceMembersDialog }
export default WorkspaceMembersDialog
